import { resolveChatSurface, createEngine, validateInput, matchesDomain } from "@merlin/flow-engine";
const TILE = 320;
const GLYPHS = [
  // estrela
  { d: "M12 3l2.6 5.6 6 .9-4.4 4.1 1.1 6-5.3-3-5.3 3 1.1-6L3.4 9.5l6-.9z", t: "translate(22 28) scale(1.15)" },
  // balão de conversa
  { d: "M3 5h18v12H11l-5 4v-4H3z", t: "translate(98 20) rotate(-8) scale(1.25)" },
  // coração
  { d: "M12 20C5 15 4 10 7 8c2-1.4 4 0 5 1.6C13 8 15 6.6 17 8c3 2 2 7-5 12z", t: "translate(178 34) scale(1.1)" },
  // nota musical
  { d: "M9 17V6l8-2v3l-8 2M9 17a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0", t: "translate(252 24) rotate(10) scale(1.1)" },
  // caneca
  { d: "M5 8h12v6a5 5 0 01-5 5h-2a5 5 0 01-5-5zM17 9h2a2 2 0 010 4h-2M4 21h14", t: "translate(56 92) scale(1.15)" },
  // sol
  { d: "M12 7a5 5 0 100 10 5 5 0 000-10M12 1.5v3M12 19.5v3M1.5 12h3M19.5 12h3M4.6 4.6l2 2M17.4 17.4l2 2M19.4 4.6l-2 2M6.6 17.4l-2 2", t: "translate(136 100) scale(1.05)" },
  // folha
  { d: "M4 20C4 10 10 4 20 4c0 10-6 16-16 16M8 16c2-4 5-6 9-7", t: "translate(214 86) rotate(-12) scale(1.2)" },
  // relógio
  { d: "M12 3a9 9 0 100 18 9 9 0 000-18M12 8v5l3.5 2", t: "translate(266 128) scale(1.05)" },
  // avião de papel
  { d: "M2 12L22 3l-7 18-3-7zM12 14l10-11", t: "translate(24 156) rotate(6) scale(1.25)" },
  // nuvem
  { d: "M6 17h11a4 4 0 00.5-8A6 6 0 006 9a4 4 0 000 8", t: "translate(104 170) scale(1.2)" },
  // presente
  { d: "M3 9h18v11H3zM3 9l2-4h5l2 4M21 9l-2-4h-5l-2 4M12 9v11", t: "translate(188 158) rotate(-6) scale(1.1)" },
  // lâmpada
  { d: "M9.5 21h5M10 18h4M12 3a6 6 0 00-3 11v4h6v-4a6 6 0 00-3-11", t: "translate(58 230) scale(1.2)" },
  // câmera
  { d: "M3 8h4l2-2h6l2 2h4v11H3zM12 17a3.5 3.5 0 100-7 3.5 3.5 0 000 7", t: "translate(138 242) rotate(8) scale(1.1)" },
  // guarda-chuva
  { d: "M12 4a9 9 0 019 8H3a9 9 0 019-8M12 12v6a2.5 2.5 0 004 1.5", t: "translate(220 224) scale(1.15)" },
  // espiral
  { d: "M14 12a3 3 0 10-3 3c3 0 5-2 5-5a6 6 0 10-6 6", t: "translate(266 262) scale(1.1)" }
];
function encodeSvg(svg) {
  return svg.replace(/\s+/g, " ").trim().replace(/%/g, "%25").replace(/#/g, "%23").replace(/</g, "%3C").replace(/>/g, "%3E").replace(/"/g, "%22");
}
function doodleSvg(color, opacity) {
  const paths = GLYPHS.map((glyph) => `<path d='${glyph.d}' transform='${glyph.t}'/>`).join("");
  return `<svg xmlns='http://www.w3.org/2000/svg' width='${TILE}' height='${TILE}' viewBox='0 0 ${TILE} ${TILE}' fill='none' stroke='${color}' stroke-opacity='${opacity}' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'>${paths}</svg>`;
}
function doodleDataUri(color, opacity) {
  return `url("data:image/svg+xml,${encodeSvg(doodleSvg(color, opacity))}")`;
}
const DOODLE_TILE_PX = TILE;
const SAFE_COLOR = /^(#[0-9a-fA-F]{3,8}|rgba?\([\d.\s,%/]+\)|[a-zA-Z]{3,24})$/;
function safeColor(value, fallback) {
  if (!value) return fallback;
  const trimmed = value.trim();
  return SAFE_COLOR.test(trimmed) ? trimmed : fallback;
}
function clampOffset(value) {
  if (!Number.isFinite(value)) return 24;
  return Math.min(Math.max(Math.trunc(value), 0), 400);
}
const TOP_BREATHING_ROOM = 24;
const MAX_SIDE_MARGIN = 24;
function panelSideMargin(offsetX) {
  return Math.min(clampOffset(offsetX), MAX_SIDE_MARGIN);
}
function panelBottomReserve(offsetY) {
  return clampOffset(offsetY) + TOP_BREATHING_ROOM;
}
const ASSET_PATH = "/widget/assets/";
function backgroundLayers(surface, assetBase) {
  const pattern = surface.pattern;
  if (!pattern) return "";
  if (pattern.kind === "doodle") {
    return `
  background-image: ${doodleDataUri(safeColor(pattern.color, "#ffffff"), pattern.opacity)};
  background-repeat: repeat;
  background-size: ${DOODLE_TILE_PX}px ${DOODLE_TILE_PX}px;`;
  }
  const base = assetBase.replace(/\/$/, "");
  if (!base || !/^https?:\/\//i.test(base)) return "";
  const veil = safeColor(pattern.veil, surface.canvas);
  const url = `${base}${ASSET_PATH}${encodeURIComponent(pattern.file)}`;
  return `
  background-image: linear-gradient(${veil}, ${veil}), url("${url}");
  background-repeat: repeat;`;
}
function buildStyles(appearance, assetBase = "") {
  const surface = resolveChatSurface(appearance.chatBackground);
  const canvas = safeColor(surface.canvas, "#ffffff");
  const chrome = safeColor(surface.surface, "#ffffff");
  const chromeBorder = safeColor(surface.surfaceBorder, "#e5e7eb");
  const inputBg = safeColor(surface.inputBg, "#ffffff");
  const inputBorder = safeColor(surface.inputBorder, "#d1d5db");
  const muted = safeColor(surface.muted, "#94a3b8");
  const danger = safeColor(surface.danger, "#b91c1c");
  const textura = backgroundLayers(surface, assetBase);
  const primary = safeColor(appearance.primaryColor, "#25D366");
  const bubble = safeColor(appearance.bubbleColor, "#f1f5f9");
  const text = safeColor(appearance.textColor, "#0f172a");
  const outBubble = safeColor(surface.outgoing?.bubble, primary);
  const outText = safeColor(surface.outgoing?.text, "#ffffff");
  const outMeta = safeColor(surface.outgoing?.meta, "rgba(255,255,255,0.75)");
  const metaColor = safeColor(surface.meta, "rgba(15,23,42,0.45)");
  const tickColor = safeColor(surface.tick, "#53bdeb");
  const headerBg = safeColor(surface.header ?? void 0, primary);
  const avatarBg = safeColor(surface.avatar ?? void 0, "rgba(255,255,255,0.22)");
  const offsetX = clampOffset(appearance.offsetX);
  const offsetY = clampOffset(appearance.offsetY);
  const sideMargin = panelSideMargin(offsetX);
  const bottomReserve = panelBottomReserve(offsetY);
  const vertical = appearance.position.startsWith("top") ? "top" : "bottom";
  const horizontal = appearance.position.endsWith("left") ? "left" : "right";
  const panelOrigin = `${vertical === "top" ? "top" : "bottom"} ${horizontal}`;
  return `
:host { all: initial; }
* { box-sizing: border-box; }

.root {
  position: fixed;
  ${vertical}: ${offsetY}px;
  ${horizontal}: ${offsetX}px;
  z-index: 2147483000;
  display: flex;
  /*
   * A ordem no DOM e' [janela, botao]. Ancorado embaixo, a janela precisa ficar acima do botao —
   * ou seja, a ordem visual e' a mesma do DOM, o que pede "column". Ancorado no topo, o botao vem
   * primeiro e a janela desce, o que pede "column-reverse". Invertido, o botao flutua acima da
   * conversa. (Sem acento grave neste comentario: ele vive dentro de um template literal.)
   */
  flex-direction: ${vertical === "top" ? "column-reverse" : "column"};
  align-items: ${horizontal === "left" ? "flex-start" : "flex-end"};
  gap: 12px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 15px;
  line-height: 1.45;
  color: ${text};
}

.launcher {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  padding: 0;
  cursor: pointer;
  background: ${primary};
  color: #fff;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.25);
  display: grid;
  place-items: center;
  overflow: hidden;
  transition: transform 120ms ease, box-shadow 120ms ease;
}
.launcher:hover { transform: scale(1.05); }
.launcher:focus-visible { outline: 3px solid ${primary}; outline-offset: 3px; }
.launcher img { width: 100%; height: 100%; object-fit: cover; display: block; }
.launcher svg { width: 32px; height: 32px; display: block; }

.panel {
  /*
   * As propriedades --merlin-viewport-width/height dizem que tamanho de tela o widget deve assumir.
   * Num site real ninguem as define, e vale 100vw/100vh — o correto la'.
   *
   * Quem as escreve e' o **proprio widget**, em publicarViewport (ui/widget.ts), sempre que houver um
   * "container" explicito: ele ja' media essa caixa para decidir o modo compacto, e o CSS estava
   * jogando a medida fora. Propriedade customizada atravessa a fronteira do Shadow DOM, entao basta
   * escrever no host. (Sem acento grave neste comentario: ele vive dentro de um template literal.)
   *
   * Sao duas contas diferentes e as duas dependem disto. A largura era o caso obvio, do preview de
   * celular: num palco de 320px, 100vw continuaria valendo a janela do navegador e a janela de chat
   * sairia com 370px, cortada. A **altura** era o caso silencioso, do preview de computador: o palco
   * e' uns 110px mais baixo que a janela (o cabecalho do painel e o respiro de cada lado), e com
   * 100vh a janela de chat, ancorada embaixo, transbordava por cima — sumia o cabecalho dela.
   *
   * A margem descontada e' o dobro de panelSideMargin(offsetX), e nao um valor fixo: a janela e'
   * ancorada a offsetX de uma borda, entao limitar a largura por outro numero deixaria os dois lados
   * com folgas diferentes. Ver panelSideMargin para a conta.
   */
  width: min(370px, calc(var(--merlin-viewport-width, 100vw) - ${sideMargin * 2}px));
  /*
   * Altura **fixa**, nao teto. Com so um max-height a janela crescia junto com o conteudo: no comeco
   * da conversa era uma caixa baixa e ia esticando a cada mensagem. Interface de celular tem altura
   * constante, e e' o que faz a janela parecer um aparelho em vez de um balao que incha.
   */
  height: min(640px, calc(var(--merlin-viewport-height, 100vh) - ${bottomReserve}px));
  background: ${chrome};
  border-radius: 16px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.28);
  display: none;
  flex-direction: column;
  overflow: hidden;
  transform-origin: ${panelOrigin};
  animation: pop 140ms ease-out;
}
.panel[data-open="true"] { display: flex; }

/*
 * Com a conversa aberta, o botao flutuante sai de cena e a janela ocupa o lugar dele.
 *
 * Vale em qualquer largura, nao so' em tela cheia: empilhado acima do botao, a janela desperdicava
 * 72px de altura (os 60 do botao mais os 12 de espacamento) e o botao ficava ali sem funcao, ja' que
 * o fechar esta' no cabecalho e o Escape tambem fecha. Ver panelBottomReserve, que deixou de
 * descontar esse espaco.
 */
.root[data-open="true"] .launcher { display: none; }

@keyframes pop {
  from { opacity: 0; transform: translateY(${vertical === "top" ? "-8px" : "8px"}) scale(0.98); }
  to { opacity: 1; transform: none; }
}

.header {
  background: ${headerBg};
  color: #fff;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
}
.header .avatar {
  width: 38px; height: 38px; border-radius: 50%;
  background: ${avatarBg};
  display: grid; place-items: center; overflow: hidden; flex: 0 0 auto;
}
.header .avatar img { width: 100%; height: 100%; object-fit: cover; }
.header .titles { flex: 1 1 auto; min-width: 0; }
.header .title { font-weight: 600; font-size: 15px; display: block; }
.header .subtitle { font-size: 12.5px; opacity: 0.9; display: block; }
.header .close {
  background: transparent; border: none; color: #fff; cursor: pointer;
  font-size: 22px; line-height: 1; padding: 4px 6px; border-radius: 6px;
}
.header .close:hover { background: rgba(255,255,255,0.18); }

/*
 * O background-attachment fica no padrao ("scroll"): a textura acompanha a caixa, nao o conteudo
 * rolado, entao ela nao desliza atras das mensagens — que e' como o WhatsApp se comporta.
 * (Sem acento grave aqui: este comentario vive dentro de um template literal.)
 */
.messages {
  flex: 1 1 auto;
  /*
   * Sem isto, um filho flex se recusa a encolher abaixo do tamanho do proprio conteudo. Com a altura
   * do painel fixa, a lista de mensagens empurraria o compositor para fora da janela em vez de rolar.
   */
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${canvas};${textura}
  scroll-behavior: smooth;
}

.bubble {
  position: relative;
  max-width: 85%;
  padding: 6px 9px 8px;
  border-radius: 7.5px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  box-shadow: 0 1px 0.5px rgba(11, 20, 26, 0.13);
}
.bubble[data-role="bot"] { background: ${bubble}; color: ${text}; align-self: flex-start; }
.bubble[data-role="visitor"] { background: ${outBubble}; color: ${outText}; align-self: flex-end; }
.bubble[data-role="system"] {
  align-self: center; background: #fff7ed; color: #9a3412;
  border: 1px solid #fed7aa; font-size: 13px; max-width: 100%; box-shadow: none;
}

/*
 * Rabicho: triangulo de 8px no canto de cima, so na **primeira** bolha de uma sequencia do mesmo
 * lado — e' assim que o WhatsApp agrupa. Cabe nos 16px de padding do .messages, entao nao e'
 * recortado nem cria barra de rolagem horizontal.
 */
.bubble[data-first="true"] { border-top-left-radius: 0; }
.bubble[data-role="visitor"][data-first="true"] { border-top-left-radius: 7.5px; border-top-right-radius: 0; }

.bubble[data-first="true"]::before {
  content: "";
  position: absolute;
  top: 0;
  width: 0;
  height: 0;
  border-top: 8px solid ${bubble};
}
.bubble[data-role="bot"][data-first="true"]::before {
  left: -8px;
  border-left: 8px solid transparent;
}
.bubble[data-role="visitor"][data-first="true"]::before {
  right: -8px;
  border-top-color: ${outBubble};
  border-right: 8px solid transparent;
}
.bubble[data-role="system"][data-first="true"]::before { display: none; }
.bubble[data-role="system"][data-first="true"] { border-radius: 7.5px; }

/*
 * Horario dentro da bolha. O float e' o que reproduz o comportamento do WhatsApp de graca: em
 * mensagem curta ele fica na mesma linha do texto, em mensagem longa desce sozinho para a linha de
 * baixo, sempre encostado a' direita.
 */
.meta {
  float: right;
  margin: 6px -3px -3px 8px;
  font-size: 11px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: ${metaColor};
  user-select: none;
}
.bubble[data-role="visitor"] .meta { color: ${outMeta}; }
.meta svg { width: 15px; height: 15px; display: block; color: ${tickColor}; }

.typing { display: inline-flex; gap: 4px; align-items: center; }
.typing span {
  width: 6px; height: 6px; border-radius: 50%; background: currentColor; opacity: 0.45;
  animation: blink 1s infinite ease-in-out;
}
.typing span:nth-child(2) { animation-delay: 0.15s; }
.typing span:nth-child(3) { animation-delay: 0.3s; }
@keyframes blink { 0%, 60%, 100% { opacity: 0.25; } 30% { opacity: 0.9; } }

.composer { flex: 0 0 auto; border-top: 1px solid ${chromeBorder}; padding: 12px; background: ${chrome}; }
.options { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
.options:empty { display: none; margin: 0; }
.option {
  background: ${chrome}; border: 1.5px solid ${primary}; color: ${primary};
  border-radius: 999px; padding: 7px 14px; cursor: pointer; font-size: 14px; font-family: inherit;
}
.option:hover { background: ${primary}; color: #fff; }
.option:focus-visible { outline: 2px solid ${text}; outline-offset: 2px; }

.form { display: flex; gap: 8px; }
.form[hidden] { display: none; }
.input {
  flex: 1 1 auto; min-width: 0; border: 1px solid ${inputBorder}; border-radius: 10px;
  padding: 10px 12px; font-size: 15px; font-family: inherit; color: ${text}; background: ${inputBg};
}
/* Sem isto, o placeholder padrao do navegador (cinza escuro) desaparece no input do tema escuro. */

.input::placeholder { color: ${muted}; opacity: 1; }
.input:focus { outline: none; border-color: ${primary}; box-shadow: 0 0 0 3px rgba(0,0,0,0.05); }
.send {
  border: none; background: ${primary}; color: #fff; border-radius: 10px;
  padding: 0 16px; cursor: pointer; font-size: 15px; font-family: inherit; font-weight: 600;
}
.send:disabled { opacity: 0.55; cursor: default; }

.hint { font-size: 12.5px; color: ${danger}; margin-top: 6px; min-height: 0; }
.hint:empty { display: none; }

.footer {
  font-size: 11.5px; color: ${muted}; text-align: center; padding: 0 12px 10px; background: ${chrome};
}
.footer[hidden] { display: none; }

/*
 * Modo compacto: em tela estreita a conversa ocupa tudo, como o app do WhatsApp.
 *
 * O gatilho e' o atributo data-compact, posto pelo JS a partir da viewport **efetiva**, e nao uma
 * media query. Motivo: o preview do painel simula um celular num palco de 390px dentro de uma janela
 * de desktop larga; media query leria a janela real e o fullscreen so existiria em producao — o pior
 * tipo de comportamento, o que nao da' para ver antes de publicar. Ver viewportSize em ui/widget.ts.
 */
/*
 * Tudo aqui exige **os dois** atributos, compacto e aberto. Com a conversa fechada, um inset zero
 * faria a raiz cobrir a tela inteira e engolir os cliques do site do cliente, e esconder o launcher
 * deixaria o visitante sem como abrir o chat. Foi o defeito da primeira versao deste bloco.
 *
 * A altura sai **so'** do inset zero, sem nenhuma unidade de viewport. Duas tentativas anteriores
 * falharam por misturar caixas: a primeira punha 100dvh no painel enquanto a raiz usava inset, e a
 * segunda punha 100dvh na propria raiz. Nos dois casos a caixa ficava menor que a tela — em fixed,
 * inset zero se dimensiona pelo bloco conteiner inicial, que no Android Chrome e' o viewport
 * **grande**, enquanto dvh com a barra de endereco a' mostra e' uns 60 a 90px menor. A diferenca
 * aparecia como uma faixa da pagina do cliente embaixo da conversa.
 *
 * Consequencia assumida: onde o bloco conteiner inicial e' o viewport grande, a base do painel pode
 * ficar atras da barra do navegador enquanto ela esta' visivel. As mensagens rolam e o compositor ja'
 * reserva a area segura, entao o custo e' pequeno perto de deixar o site do cliente aparecendo.
 * (Sem acento grave aqui: este comentario vive dentro de um template literal.)
 */
.root[data-compact="true"][data-open="true"] {
  inset: 0;
  gap: 0;
  align-items: stretch;
}

/*
 * O painel preenche a **raiz**, e nao uma unidade de viewport propria. Defeito real que isto
 * conserta: a raiz se dimensiona por inset zero (bloco conteiner inicial) e o painel usava 100dvh,
 * o viewport dinamico, que encolhe enquanto a barra de endereco esta' visivel. As duas caixas
 * mediam coisas diferentes, e sobrava uma faixa de uns 60px embaixo mostrando a pagina do cliente.
 *
 * Com height de 100% existe uma medida so. Quem acompanha a barra do navegador e' a raiz, logo
 * abaixo — e o painel vai junto de graca.
 * (Sem acento grave neste comentario: ele vive dentro de um template literal.)
 */
.root[data-compact="true"][data-open="true"] .panel {
  width: 100%;
  height: 100%;
  max-width: none;
  border-radius: 0;
  box-shadow: none;
  animation: subir 180ms ease-out;
}


/* Barra de gestos do iPhone comeria o campo de texto sem isto. */
.root[data-compact="true"][data-open="true"] .composer {
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
}
.root[data-compact="true"][data-open="true"] .header {
  padding-top: calc(14px + env(safe-area-inset-top, 0px));
}

@keyframes subir {
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) {
  .panel { animation: none; }
  .launcher { transition: none; }
  .typing span { animation: none; }
}
`;
}
const WORKER_SOURCE = `
(function () {
  // Fecha os caminhos de rede: o worker herda a origem da página host.
  var blocked = ['fetch','XMLHttpRequest','WebSocket','EventSource','importScripts','indexedDB','caches','Notification','SharedWorker','Worker'];
  for (var i = 0; i < blocked.length; i++) {
    try { self[blocked[i]] = undefined; } catch (e) {}
  }
  try { if (self.navigator && self.navigator.sendBeacon) self.navigator.sendBeacon = undefined; } catch (e) {}

  function describe(error) {
    if (!error) return 'Erro desconhecido no código.';
    if (error.message) return String(error.message);
    return String(error);
  }

  // O valor precisa atravessar o postMessage e caber no JSON das variáveis do fluxo.
  function normalize(value) {
    if (value === undefined) return null;
    try { return JSON.parse(JSON.stringify(value)); } catch (e) { return String(value); }
  }

  self.onmessage = function (event) {
    var payload = event.data || {};
    try {
      var fn = new Function('vars', '"use strict";' + payload.code);
      Promise.resolve(fn(payload.variables || {})).then(
        function (value) { self.postMessage({ ok: true, value: normalize(value) }); },
        function (error) { self.postMessage({ ok: false, error: describe(error) }); }
      );
    } catch (error) {
      self.postMessage({ ok: false, error: describe(error) });
    }
  };
})();
`;
const CSP_WORKER_HINT = "O navegador bloqueou a criação do Web Worker. Normalmente é o Content-Security-Policy do site (`worker-src`/`script-src` sem `blob:`). Libere `blob:` para o widget ou troque este bloco para o modo `page`.";
const CSP_EVAL_HINT = "O navegador bloqueou a execução do código. Normalmente é o Content-Security-Policy do site (`script-src` sem `unsafe-eval`). Sem isso, o bloco de código não roda nesta página.";
function describeError(error) {
  if (error instanceof Error && error.message) return error.message;
  return String(error);
}
function runInSandbox(code, variables, timeoutMs) {
  return new Promise((resolve) => {
    let worker;
    let blobUrl = "";
    try {
      blobUrl = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: "text/javascript" }));
      worker = new Worker(blobUrl);
    } catch (error) {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      resolve({ ok: false, error: `${CSP_WORKER_HINT} (${describeError(error)})` });
      return;
    }
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      worker.terminate();
      URL.revokeObjectURL(blobUrl);
      resolve(result);
    };
    const timer = setTimeout(() => {
      finish({ ok: false, error: `O código não terminou em ${timeoutMs}ms e foi interrompido.` });
    }, timeoutMs);
    worker.onmessage = (event) => {
      const data = event.data;
      finish(
        data && typeof data === "object" && "ok" in data ? data : { ok: false, error: "O worker devolveu um resultado inesperado." }
      );
    };
    worker.onerror = (event) => {
      finish({ ok: false, error: event.message || "Falha ao executar o código no sandbox." });
    };
    try {
      worker.postMessage({ code, variables });
    } catch (error) {
      finish({ ok: false, error: `Não foi possível enviar as variáveis ao sandbox: ${describeError(error)}` });
    }
  });
}
async function runInPage(code, variables, timeoutMs) {
  let fn;
  try {
    fn = new Function("vars", `"use strict";${code}`);
  } catch (error) {
    const message = describeError(error);
    const isCsp = error instanceof EvalError || /unsafe-eval|Content Security Policy|CSP/i.test(message);
    return { ok: false, error: isCsp ? `${CSP_EVAL_HINT} (${message})` : message };
  }
  try {
    const value = await Promise.race([
      Promise.resolve(fn(variables)),
      new Promise(
        (_, reject) => setTimeout(
          () => reject(new Error(`O código não terminou em ${timeoutMs}ms.`)),
          timeoutMs
        )
      )
    ]);
    if (value === void 0) return { ok: true, value: null };
    try {
      return { ok: true, value: JSON.parse(JSON.stringify(value)) };
    } catch {
      return { ok: true, value: String(value) };
    }
  } catch (error) {
    return { ok: false, error: describeError(error) };
  }
}
async function runCode(effect, variables, options = {}) {
  const mode = options.forceSandbox ? "sandbox" : effect.executionMode;
  return mode === "page" ? runInPage(effect.code, variables, effect.timeoutMs) : runInSandbox(effect.code, variables, effect.timeoutMs);
}
function parseBody(text, contentType) {
  if (!text) return null;
  if (contentType && contentType.includes("json")) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return text;
}
function snippet(text, max = 200) {
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max)}…` : clean;
}
async function runBrowserWebhook(request) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), request.timeoutMs);
  const headers = {};
  for (const pair of request.headers ?? []) {
    if (pair.key) headers[pair.key] = pair.value;
  }
  const hasBody = request.method !== "GET" && request.body !== void 0;
  if (hasBody) headers["Content-Type"] = request.contentType;
  try {
    const response = await fetch(request.url, {
      method: request.method,
      headers,
      ...hasBody ? { body: request.body } : {},
      signal: controller.signal,
      // Sem cookies do destino: o visitante não deve autenticar acidentalmente numa API de terceiro.
      credentials: "omit",
      mode: "cors",
      redirect: "follow"
    });
    const text = await response.text();
    const value = parseBody(text, response.headers.get("content-type"));
    if (!response.ok) {
      return {
        ok: false,
        error: `O webhook respondeu ${response.status}. ${snippet(text)}`
      };
    }
    return { ok: true, value };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return { ok: false, error: `O webhook não respondeu em ${request.timeoutMs}ms.` };
    }
    const message = error instanceof Error ? error.message : String(error);
    return {
      ok: false,
      error: `Não foi possível chamar ${request.url}. Causa provável: o destino não libera CORS para este site (precisa de Access-Control-Allow-Origin), ou o endereço está inacessível. (${message})`
    };
  } finally {
    clearTimeout(timer);
  }
}
async function runBackendWebhook(options) {
  const controller = new AbortController();
  const timeoutMs = options.timeoutMs ?? 2e4;
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(
      `${options.apiBase.replace(/\/$/, "")}/runWebhook?key=${encodeURIComponent(options.publicKey)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nodeId: options.nodeId,
          variables: options.variables,
          sessionId: options.sessionId
        }),
        signal: controller.signal,
        credentials: "omit",
        mode: "cors"
      }
    );
    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload) {
      const detail = payload?.message ?? payload?.error ?? `HTTP ${response.status}`;
      return { ok: false, error: `Webhook de backend falhou: ${detail}` };
    }
    if (typeof payload.status === "number" && payload.status >= 400) {
      return {
        ok: false,
        error: `O destino respondeu ${payload.status}. ${snippet(JSON.stringify(payload.body ?? ""))}`
      };
    }
    return { ok: true, value: payload.body ?? null };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return { ok: false, error: `O webhook de backend não respondeu em ${timeoutMs}ms.` };
    }
    return {
      ok: false,
      error: `Não foi possível falar com o backend do webchat. (${error instanceof Error ? error.message : String(error)})`
    };
  } finally {
    clearTimeout(timer);
  }
}
async function fetchConfig(apiBase, publicKey, options = {}) {
  const { timeoutMs = 1e4, noCache = false } = typeof options === "number" ? { timeoutMs: options } : options;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const base = `${apiBase.replace(/\/$/, "")}/getConfig?key=${encodeURIComponent(publicKey)}`;
  const url = noCache ? `${base}&_=${Date.now()}` : base;
  try {
    const response = await fetch(url, {
      method: "GET",
      credentials: "omit",
      mode: "cors",
      signal: controller.signal,
      ...noCache ? { cache: "no-store" } : {}
    });
    const tipoDoConteudo = response.headers.get("content-type") ?? "";
    if (tipoDoConteudo.includes("text/html")) {
      return {
        ok: false,
        status: response.status,
        error: "not_json",
        message: `${apiBase} respondeu HTML, não JSON (HTTP ${response.status}). Isso costuma significar que data-api-base está errado ou faltando no script do widget.`
      };
    }
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        error: payload?.error ?? "http_error",
        message: payload?.message ?? `A requisição de config falhou com HTTP ${response.status}.`
      };
    }
    if (!payload?.config) {
      return {
        ok: false,
        status: response.status,
        error: "invalid_payload",
        message: "O backend respondeu sem a config."
      };
    }
    return { ok: true, config: payload.config };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return { ok: false, status: 0, error: "timeout", message: `A config não chegou em ${timeoutMs}ms.` };
    }
    return {
      ok: false,
      status: 0,
      error: "network",
      message: `Não foi possível buscar a config em ${url}. (${error instanceof Error ? error.message : String(error)})`
    };
  } finally {
    clearTimeout(timer);
  }
}
function createSessionId() {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") return cryptoObj.randomUUID();
  return `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}
const SDK_VERSION = "0.19.0";
const DEFAULT_FLUSH_DELAY_MS = 2500;
function noop() {
  return {
    start: () => {
    },
    node: () => {
    },
    complete: () => {
    },
    submitLead: () => {
    },
    destroy: () => {
    }
  };
}
function createTelemetry(options) {
  if (!options.enabled || !options.apiBase || !options.publicKey) return noop();
  const eventsUrl = `${options.apiBase.replace(/\/$/, "")}/events?key=${encodeURIComponent(options.publicKey)}`;
  const leadsUrl = `${options.apiBase.replace(/\/$/, "")}/leads?key=${encodeURIComponent(options.publicKey)}`;
  const flushDelay = options.flushDelayMs ?? DEFAULT_FLUSH_DELAY_MS;
  let pending = [];
  let timer = null;
  let started = false;
  let finished = false;
  let lastNodeId = null;
  let destroyed = false;
  function send(body, useBeacon) {
    const payload = JSON.stringify(body);
    if (useBeacon && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      try {
        const blob = new Blob([payload], { type: "text/plain;charset=UTF-8" });
        if (navigator.sendBeacon(eventsUrl, blob)) return;
      } catch {
      }
    }
    void fetch(eventsUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      credentials: "omit",
      mode: "cors",
      keepalive: useBeacon
    }).catch(() => {
    });
  }
  function flush(useBeacon = false) {
    if (pending.length === 0) return;
    const events = pending;
    pending = [];
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    send({ sessionId: options.sessionId, sdkVersion: SDK_VERSION, events }, useBeacon);
  }
  function enqueue(event, immediate = false) {
    if (destroyed) return;
    pending.push(event);
    if (immediate) {
      flush();
      return;
    }
    if (!timer) timer = setTimeout(() => flush(), flushDelay);
  }
  function onLeaving() {
    if (!started || finished) return;
    finished = true;
    enqueue({ type: "abandon", ...lastNodeId ? { nodeId: lastNodeId } : {} });
    flush(true);
  }
  const onVisibility = () => {
    if (document.visibilityState === "hidden") onLeaving();
  };
  window.addEventListener("pagehide", onLeaving);
  document.addEventListener("visibilitychange", onVisibility);
  return {
    start() {
      if (started) return;
      started = true;
      finished = false;
      enqueue({ type: "start" });
    },
    node(nodeId) {
      if (!nodeId || nodeId === lastNodeId) return;
      lastNodeId = nodeId;
      enqueue({ type: "node", nodeId });
    },
    complete() {
      if (finished) return;
      finished = true;
      enqueue({ type: "complete" }, true);
    },
    submitLead(variables, transcript) {
      if (destroyed) return;
      if (!variables || Object.keys(variables).length === 0) return;
      void fetch(leadsUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: options.sessionId,
          sdkVersion: SDK_VERSION,
          variables,
          // Só as falas, sem estrutura interna: é o que dá contexto a quem for atender.
          transcript: transcript.map((entry) => ({ role: entry.role, text: entry.text })),
          pageUrl: location.href
        }),
        credentials: "omit",
        mode: "cors",
        keepalive: true
      }).catch(() => {
      });
    },
    destroy() {
      destroyed = true;
      flush();
      window.removeEventListener("pagehide", onLeaving);
      document.removeEventListener("visibilitychange", onVisibility);
    }
  };
}
const WHATSAPP_GLYPH = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.66.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.27-.47-2.42-1.49-.9-.8-1.5-1.79-1.68-2.09-.17-.3-.02-.46.13-.61.15-.15.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.18-.24-.58-.48-.5-.66-.5h-.56c-.2 0-.5.07-.77.37-.27.3-1.02 1-1.02 2.42s1.04 2.8 1.19 3c.15.2 2.05 3.28 5.02 4.47 2.97 1.19 2.97.79 3.5.74.54-.05 1.75-.72 2-1.41.24-.7.24-1.29.17-1.41-.07-.12-.27-.2-.57-.35z"/><path d="M12 2C6.48 2 2 6.48 2 12c0 1.85.5 3.58 1.38 5.06L2 22l5.09-1.33A9.94 9.94 0 0 0 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm0 18.2c-1.6 0-3.09-.46-4.35-1.26l-.31-.19-3.02.79.8-2.94-.2-.32A8.15 8.15 0 0 1 3.8 12c0-4.52 3.68-8.2 8.2-8.2s8.2 3.68 8.2 8.2-3.68 8.2-8.2 8.2z"/></svg>';
const DOUBLE_TICK_GLYPH = '<svg viewBox="0 0 16 15" fill="currentColor" aria-hidden="true"><path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185c.143.14.361.125.484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"/></svg>';
const INPUT_TYPE_ATTR = {
  text: "text",
  email: "email",
  phone: "tel",
  number: "text"
};
function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== void 0) node.textContent = text;
  return node;
}
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
function mountWidget(options) {
  const { config } = options;
  const mode = options.mode ?? "live";
  const container = options.container ?? document.body;
  const appearance = config.appearance;
  const emit = (event) => options.onEvent?.(event);
  const host = el("div");
  host.setAttribute("data-merlin-webchat", config.publicKey);
  host.setAttribute("data-merlin-config", String(config.version ?? 0));
  host.setAttribute("data-merlin-sdk", SDK_VERSION);
  const shadow = host.attachShadow({ mode: "closed" });
  const assetBase = options.assetBase ?? (typeof window === "undefined" ? "" : window.location.origin);
  const style = document.createElement("style");
  style.textContent = buildStyles(appearance, assetBase);
  shadow.appendChild(style);
  const root = el("div", "root");
  root.setAttribute("data-position", appearance.position);
  const launcher = el("button", "launcher");
  launcher.type = "button";
  launcher.setAttribute("aria-label", `Abrir conversa: ${appearance.title}`);
  if (appearance.imageUrl) {
    const img = el("img");
    img.src = appearance.imageUrl;
    img.alt = "";
    launcher.appendChild(img);
  } else {
    launcher.innerHTML = WHATSAPP_GLYPH;
  }
  const panel = el("div", "panel");
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-label", appearance.title);
  panel.dataset.open = "false";
  root.dataset.open = "false";
  const header = el("div", "header");
  const avatar = el("div", "avatar");
  if (appearance.imageUrl) {
    const img = el("img");
    img.src = appearance.imageUrl;
    img.alt = "";
    avatar.appendChild(img);
  } else {
    avatar.innerHTML = WHATSAPP_GLYPH;
  }
  const titles = el("div", "titles");
  titles.appendChild(el("span", "title", appearance.title));
  if (appearance.subtitle) titles.appendChild(el("span", "subtitle", appearance.subtitle));
  const closeButton = el("button", "close", "×");
  closeButton.type = "button";
  closeButton.setAttribute("aria-label", "Fechar conversa");
  header.append(avatar, titles, closeButton);
  const messages = el("div", "messages");
  messages.setAttribute("role", "log");
  messages.setAttribute("aria-live", "polite");
  const composer = el("div", "composer");
  const optionsRow = el("div", "options");
  const form = el("form", "form");
  const input = el("input", "input");
  input.type = "text";
  input.autocomplete = "off";
  const send = el("button", "send", "Enviar");
  send.type = "submit";
  form.append(input, send);
  const hint = el("div", "hint");
  composer.append(optionsRow, form, hint);
  const footer = el("div", "footer");
  footer.hidden = true;
  panel.append(header, messages, composer, footer);
  root.append(panel, launcher);
  shadow.appendChild(root);
  container.appendChild(host);
  let engine = createEngine(config);
  let sessionId = createSessionId();
  let telemetry = createTelemetry({
    apiBase: options.apiBase ?? "",
    publicKey: config.publicKey,
    sessionId,
    enabled: mode === "live" && Boolean(options.apiBase)
  });
  let started = false;
  let open = false;
  let generation = 0;
  let destroyed = false;
  let awaitingInput = null;
  const timers = /* @__PURE__ */ new Set();
  const COMPACT_MAX_WIDTH = 480;
  let compact = false;
  let observadorDePalco = null;
  let overflowAnterior = null;
  function palcoMedivel() {
    const palco = options.container;
    return palco && palco.clientWidth > 0 ? palco : null;
  }
  function viewportSize() {
    const palco = palcoMedivel();
    if (palco) return { width: palco.clientWidth, height: palco.clientHeight };
    if (typeof window === "undefined") return { width: Number.POSITIVE_INFINITY, height: 0 };
    return { width: window.innerWidth, height: window.innerHeight };
  }
  function publicarViewport() {
    const palco = palcoMedivel();
    if (!palco) {
      host.style.removeProperty("--merlin-viewport-width");
      host.style.removeProperty("--merlin-viewport-height");
      return;
    }
    host.style.setProperty("--merlin-viewport-width", `${palco.clientWidth}px`);
    host.style.setProperty("--merlin-viewport-height", `${palco.clientHeight}px`);
  }
  function travarRolagem(travar) {
    if (typeof document === "undefined" || mode !== "live") return;
    if (travar) {
      if (overflowAnterior === null) overflowAnterior = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return;
    }
    if (overflowAnterior !== null) {
      document.body.style.overflow = overflowAnterior;
      overflowAnterior = null;
    }
  }
  function syncCompact() {
    publicarViewport();
    compact = viewportSize().width <= COMPACT_MAX_WIDTH;
    if (compact) root.dataset.compact = "true";
    else delete root.dataset.compact;
    travarRolagem(compact && open);
  }
  function track(promise) {
    return promise;
  }
  function scheduleTimer(fn, ms) {
    const timer = setTimeout(() => {
      timers.delete(timer);
      fn();
    }, ms);
    timers.add(timer);
  }
  function scrollToEnd() {
    messages.scrollTop = messages.scrollHeight;
  }
  function abreSequencia(role) {
    const anteriores = [...messages.children].filter((node) => !node.querySelector(".typing"));
    const ultima = anteriores[anteriores.length - 1];
    return ultima?.dataset.role !== role;
  }
  function horaAgora() {
    return (/* @__PURE__ */ new Date()).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }
  function addBubble(role, text) {
    const bubble = el("div", "bubble");
    bubble.dataset.role = role;
    if (abreSequencia(role)) bubble.dataset.first = "true";
    const corpo = el("span", "texto");
    corpo.textContent = text;
    bubble.appendChild(corpo);
    if (role !== "system") {
      const meta = el("span", "meta");
      meta.append(horaAgora());
      if (role === "visitor") {
        const tique = el("span");
        tique.innerHTML = DOUBLE_TICK_GLYPH;
        meta.appendChild(tique);
      }
      bubble.appendChild(meta);
    }
    messages.appendChild(bubble);
    scrollToEnd();
    return bubble;
  }
  function showTyping() {
    const bubble = el("div", "bubble");
    bubble.dataset.role = "bot";
    const typing = el("div", "typing");
    typing.append(el("span"), el("span"), el("span"));
    bubble.appendChild(typing);
    messages.appendChild(bubble);
    scrollToEnd();
    return bubble;
  }
  function setComposer(state) {
    form.hidden = state !== "input";
    send.disabled = state !== "input";
    input.disabled = state !== "input";
    if (state !== "choice") optionsRow.replaceChildren();
    if (state !== "input") {
      input.value = "";
      hint.textContent = "";
      awaitingInput = null;
    }
  }
  function finish(reason) {
    setComposer("ended");
    footer.hidden = false;
    footer.replaceChildren();
    footer.appendChild(document.createTextNode(reason));
    const restart = el("button", "option", "Começar de novo");
    restart.type = "button";
    restart.style.marginTop = "8px";
    restart.addEventListener("click", () => instance.reset());
    footer.appendChild(el("div"));
    footer.appendChild(restart);
  }
  async function processEffects(effects, myGeneration) {
    for (const effect of effects) {
      if (destroyed || myGeneration !== generation) return;
      if ("nodeId" in effect && effect.nodeId) telemetry.node(effect.nodeId);
      switch (effect.kind) {
        case "message": {
          const typingDelay = Math.min(appearance.typingDelayMs ?? 0, 3e3);
          if (typingDelay > 0) {
            const placeholder = showTyping();
            await track(delay(typingDelay));
            if (destroyed || myGeneration !== generation) return;
            placeholder.remove();
          }
          addBubble("bot", effect.text);
          break;
        }
        case "awaitInput": {
          addBubble("bot", effect.prompt);
          awaitingInput = { saveAs: effect.saveAs, inputType: effect.inputType };
          setComposer("input");
          input.type = INPUT_TYPE_ATTR[effect.inputType] ?? "text";
          input.placeholder = effect.placeholder ?? "Escreva sua resposta…";
          if (open) input.focus();
          return;
        }
        case "awaitChoice": {
          addBubble("bot", effect.prompt);
          setComposer("choice");
          optionsRow.replaceChildren();
          for (const option of effect.options) {
            const button = el("button", "option", option.label);
            button.type = "button";
            button.addEventListener("click", () => {
              addBubble("visitor", option.label);
              optionsRow.replaceChildren();
              const next = engine.submitChoice(option.id);
              void processEffects(next, generation);
            });
            optionsRow.appendChild(button);
          }
          scrollToEnd();
          return;
        }
        case "runCode": {
          setComposer("waiting");
          const placeholder = showTyping();
          const result = await track(
            runCode(effect, engine.getVariables(), {
              // No preview, código de terceiros não roda no contexto do painel.
              forceSandbox: mode === "preview"
            })
          );
          if (destroyed || myGeneration !== generation) return;
          placeholder.remove();
          if (mode === "preview" && effect.executionMode === "page") {
            addBubble(
              "system",
              "Este bloco está em modo `page`. No preview ele roda no sandbox — no site publicado terá acesso completo à página."
            );
          }
          const next = engine.resolveEffect(effect.nodeId, result);
          await processEffects(next, myGeneration);
          return;
        }
        case "browserWebhook": {
          setComposer("waiting");
          const placeholder = showTyping();
          const result = await track(runBrowserWebhook(effect.request));
          if (destroyed || myGeneration !== generation) return;
          placeholder.remove();
          const next = engine.resolveEffect(effect.nodeId, result);
          await processEffects(next, myGeneration);
          return;
        }
        case "backendWebhook": {
          setComposer("waiting");
          const placeholder = showTyping();
          if (mode === "preview" || !options.apiBase) {
            placeholder.remove();
            addBubble(
              "system",
              mode === "preview" ? "Webhook de backend não roda no preview: o servidor executa a partir da versão publicada." : "Webhook de backend indisponível: o widget foi montado sem `apiBase`."
            );
            const next2 = engine.resolveEffect(effect.nodeId, { ok: true, value: null });
            await processEffects(next2, myGeneration);
            return;
          }
          const result = await track(
            runBackendWebhook({
              apiBase: options.apiBase,
              publicKey: config.publicKey,
              nodeId: effect.nodeId,
              variables: engine.getVariables(),
              sessionId
            })
          );
          if (destroyed || myGeneration !== generation) return;
          placeholder.remove();
          const next = engine.resolveEffect(effect.nodeId, result);
          await processEffects(next, myGeneration);
          return;
        }
        case "error": {
          addBubble("system", effect.message);
          emit({ type: "error", message: effect.message });
          break;
        }
        case "end": {
          finish("Conversa finalizada.");
          telemetry.complete();
          if (config.collectLeads !== false) {
            telemetry.submitLead(engine.getVariables(), engine.getTranscript());
          }
          emit({ type: "end", variables: engine.getVariables() });
          return;
        }
      }
    }
    emit({ type: "variables", variables: engine.getVariables() });
  }
  function submitCurrentInput() {
    if (!awaitingInput) return;
    const value = input.value;
    const validation = validateInput(awaitingInput.inputType, value);
    if (!validation.ok) {
      hint.textContent = validation.error ?? "Resposta inválida.";
      return;
    }
    hint.textContent = "";
    const trimmed = value.trim();
    addBubble("visitor", trimmed);
    input.value = "";
    setComposer("waiting");
    const next = engine.submitInput(trimmed);
    void processEffects(next, generation);
  }
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    submitCurrentInput();
  });
  input.addEventListener("input", () => {
    if (hint.textContent) hint.textContent = "";
  });
  const instance = {
    open() {
      if (destroyed) return;
      open = true;
      panel.dataset.open = "true";
      root.dataset.open = "true";
      launcher.setAttribute("aria-label", `Fechar conversa: ${appearance.title}`);
      syncCompact();
      emit({ type: "open" });
      if (!started) {
        started = true;
        telemetry.start();
        emit({ type: "start" });
        void processEffects(engine.start(), generation);
      } else if (awaitingInput) {
        input.focus();
      }
    },
    close() {
      open = false;
      panel.dataset.open = "false";
      root.dataset.open = "false";
      launcher.setAttribute("aria-label", `Abrir conversa: ${appearance.title}`);
      travarRolagem(false);
      emit({ type: "close" });
    },
    toggle() {
      if (open) instance.close();
      else instance.open();
    },
    reset() {
      generation += 1;
      engine = createEngine(config);
      telemetry.destroy();
      sessionId = createSessionId();
      telemetry = createTelemetry({
        apiBase: options.apiBase ?? "",
        publicKey: config.publicKey,
        sessionId,
        enabled: mode === "live" && Boolean(options.apiBase)
      });
      started = false;
      awaitingInput = null;
      messages.replaceChildren();
      optionsRow.replaceChildren();
      footer.hidden = true;
      footer.replaceChildren();
      setComposer("waiting");
      if (open) {
        started = true;
        telemetry.start();
        void processEffects(engine.start(), generation);
      }
    },
    destroy() {
      destroyed = true;
      telemetry.destroy();
      generation += 1;
      for (const timer of timers) clearTimeout(timer);
      timers.clear();
      travarRolagem(false);
      if (typeof window !== "undefined") window.removeEventListener("resize", syncCompact);
      observadorDePalco?.disconnect();
      host.remove();
    },
    isOpen: () => open,
    getVariables: () => engine.getVariables(),
    getTranscript: () => engine.getTranscript(),
    host,
    shadow
  };
  launcher.addEventListener("click", () => instance.toggle());
  closeButton.addEventListener("click", () => instance.close());
  panel.addEventListener("keydown", (event) => {
    if (event.key === "Escape") instance.close();
  });
  syncCompact();
  if (typeof window !== "undefined") window.addEventListener("resize", syncCompact);
  observadorDePalco = options.container && typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => syncCompact()) : null;
  observadorDePalco?.observe(options.container);
  setComposer("waiting");
  const autoOpenDelay = options.autoOpen ? 0 : appearance.autoOpenDelayMs ?? 0;
  if (options.autoOpen) {
    instance.open();
  } else if (autoOpenDelay > 0) {
    scheduleTimer(() => {
      if (!destroyed && !open) instance.open();
    }, autoOpenDelay);
  }
  return instance;
}
const LOG_PREFIX = "[merlin-webchat]";
function findScriptTag() {
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement && current.dataset.webchatId) return current;
  return document.querySelector("script[data-webchat-id]");
}
function resolveAssetBase(script) {
  try {
    return script?.src ? new URL(script.src).origin : "";
  } catch {
    return "";
  }
}
function resolveApiBase(script) {
  const explicit = script?.dataset.apiBase?.trim();
  if (explicit) return { base: explicit.replace(/\/$/, ""), adivinhado: false };
  const src = script?.src;
  if (!src) return null;
  try {
    return { base: `${new URL(src).origin}/api`, adivinhado: true };
  } catch {
    return null;
  }
}
function whenDomReady() {
  if (document.readyState === "loading") {
    return new Promise((resolve) => {
      document.addEventListener("DOMContentLoaded", () => resolve(), { once: true });
    });
  }
  return Promise.resolve();
}
async function bootstrap() {
  const script = findScriptTag();
  const publicKey = script?.dataset.webchatId?.trim();
  if (!publicKey) {
    console.error(
      `${LOG_PREFIX} não encontrei o atributo data-webchat-id. Exemplo: <script src=".../widget.js" data-webchat-id="pk_sua_chave" async><\/script>`
    );
    return null;
  }
  const resolvida = resolveApiBase(script);
  if (!resolvida) {
    console.error(`${LOG_PREFIX} não consegui descobrir a base da API. Informe data-api-base no script.`);
    return null;
  }
  const apiBase = resolvida.base;
  if (resolvida.adivinhado) {
    console.warn(
      `${LOG_PREFIX} sem data-api-base no script; vou tentar ${apiBase}. Se a API não estiver nesse host, informe data-api-base — o painel gera o snippet completo. Erro de CORS logo abaixo costuma ser este atributo faltando, não configuração de CORS.`
    );
  }
  const result = await fetchConfig(apiBase, publicKey, {
    noCache: script?.dataset.noCache === "true"
  });
  if (!result.ok) {
    if (result.status === 403 && result.error === "webchat_suspended") {
      console.warn(
        `${LOG_PREFIX} ${result.message}
A recusa fica em cache por até uma hora, então o widget pode levar esse tempo para voltar depois da reativação.`
      );
    } else if (result.status === 403) {
      console.error(
        `${LOG_PREFIX} o backend recusou a config para este domínio (HTTP 403: ${result.error}).
${result.message}
Domínio atual: ${window.location.hostname}. Adicione-o em "Domínios autorizados" no painel do webchat e publique novamente.`
      );
    } else if (result.status === 404 && result.error === "not_published") {
      console.warn(`${LOG_PREFIX} este webchat ainda não foi publicado. Clique em "Publicar" no painel.`);
    } else if (result.status === 429 && (result.error === "quota_exceeded" || result.error === "request_limit_exceeded")) {
      console.warn(
        `${LOG_PREFIX} ${result.message}
O widget não será exibido até o limite ser aumentado ou o mês virar. Depois de aumentar, a recusa ainda pode ficar em cache por até um minuto.`
      );
    } else {
      console.error(`${LOG_PREFIX} ${result.message}`);
    }
    return null;
  }
  const config = result.config;
  if (!matchesDomain(window.location.hostname, config.authorizedDomains)) {
    console.error(
      `${LOG_PREFIX} o domínio "${window.location.hostname}" não está na lista de domínios autorizados deste webchat (${config.authorizedDomains.join(", ")}). O widget não será exibido.`
    );
    return null;
  }
  await whenDomReady();
  const listeners = /* @__PURE__ */ new Set();
  const instance = mountWidget({
    config,
    apiBase,
    assetBase: resolveAssetBase(script),
    autoOpen: script?.dataset.autoOpen === "true",
    onEvent: (event) => {
      for (const listener of listeners) {
        try {
          listener(event);
        } catch (error) {
          console.error(`${LOG_PREFIX} erro em um listener:`, error);
        }
      }
    }
  });
  const api = {
    open: () => instance.open(),
    close: () => instance.close(),
    toggle: () => instance.toggle(),
    reset: () => instance.reset(),
    getVariables: () => instance.getVariables(),
    on(handler) {
      listeners.add(handler);
      return () => listeners.delete(handler);
    },
    ready: Promise.resolve(instance),
    version: SDK_VERSION
  };
  window.MerlinWebchat = api;
  return instance;
}
export {
  SDK_VERSION,
  bootstrap,
  buildStyles,
  createSessionId,
  createTelemetry,
  fetchConfig,
  mountWidget,
  panelBottomReserve,
  panelSideMargin,
  runBackendWebhook,
  runBrowserWebhook,
  runCode,
  runInPage,
  runInSandbox,
  safeColor
};
//# sourceMappingURL=sdk.js.map
